import { Button, ColumnLayout, FileUpload, FormField, Select, SpaceBetween, StatusIndicator, Toggle } from '@amzn/awsui-components-core';
import { FormikProps, getIn } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { LogoType } from 'src/components/context/AppContextModels';
import { ApplicationFormValues } from '../../AdminModels';
import { AppLogoService } from 'src/services/AppLogoService';
import { useLogoOptions } from './useLogoOptions';
import EmbeddedNavPreview from './EmbeddedNavPreview';

interface ApplicationLogoConfigProps {
  formik: FormikProps<ApplicationFormValues>;
}

const ApplicationLogoConfig: React.FC<ApplicationLogoConfigProps> = ({ formik }) => {
  const logoOptions = useLogoOptions();
  const sessionIdRef = useRef(crypto.randomUUID());
  const originalLogoConfigRef = useRef(formik.values.applicationDetails?.logoConfig ?? null);
  const [logoFile, setLogoFile] = useState<File[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const s3LocationKey = formik.values.applicationDetails?.logoConfig?.s3Location?.key;
  useEffect(() => {
    const s3Location = formik.values.applicationDetails?.logoConfig?.s3Location;
    if (!s3Location) return;

    let cancelled = false;
    AppLogoService.getLogoPreviewUrl(s3Location).then((url) => {
      if (!cancelled && url) setPreviewUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, [s3LocationKey]);

  const isAppSpecific = formik.values.applicationDetails?.logoConfig?.logoType === 'app-specific';

  return (
    <SpaceBetween size="m" direction="vertical">
      <ColumnLayout columns={2}>
        <SpaceBetween size="m" direction="vertical">
          <FormField label="App-Specific Upload" description="Toggle on to upload your own logo instead of using a default.">
            <Toggle
              checked={isAppSpecific}
              onChange={({ detail }) => {
                const newType: LogoType = detail.checked ? 'app-specific' : 'bundled';
                setLogoFile([]);
                setUploadError(null);

                if (detail.checked) {
                  const original = originalLogoConfigRef.current;
                  if (original?.s3Location && original?.uploadedFileName) {
                    formik.setFieldValue('applicationDetails.logoConfig', {
                      logoType: 'app-specific',
                      selectedLogoName: null,
                      uploadedFileName: original.uploadedFileName,
                      s3Location: original.s3Location
                    });
                    setPreviewUrl(null);
                  } else {
                    formik.setFieldValue('applicationDetails.logoConfig', {
                      logoType: 'app-specific',
                      selectedLogoName: null,
                      uploadedFileName: null,
                      s3Location: null
                    });
                  }
                } else {
                  setPreviewUrl(null);
                  formik.setFieldValue('applicationDetails.logoConfig', {
                    logoType: newType,
                    selectedLogoName: null,
                    uploadedFileName: null,
                    s3Location: null
                  });
                }
              }}
            >
              Use App-Specific Logo
            </Toggle>
          </FormField>

          {!isAppSpecific ? (
            <FormField
              label="Default Logo"
              description="Pick a pre-built logo from the library to represent your application."
              constraintText="This logo appears in the navigation bar and app header."
              errorText={
                getIn(formik.touched, 'applicationDetails.logoConfig.selectedLogoName') &&
                getIn(formik.errors, 'applicationDetails.logoConfig.selectedLogoName')
              }
            >
              <Select
                selectedOption={
                  formik.values.applicationDetails?.logoConfig?.selectedLogoName
                    ? {
                        value: formik.values.applicationDetails.logoConfig.selectedLogoName,
                        label: formik.values.applicationDetails.logoConfig.selectedLogoName.replace(/^(cloudscape|fa|shared):/, '')
                      }
                    : null
                }
                onChange={({ detail }) => {
                  const selectedValue = detail.selectedOption.value || '';
                  const newLogoType: LogoType = selectedValue.startsWith('shared:') ? 'shared-library' : 'bundled';
                  formik.setFieldValue('applicationDetails.logoConfig.selectedLogoName', selectedValue);
                  formik.setFieldValue('applicationDetails.logoConfig.logoType', newLogoType);
                  if (newLogoType === 'shared-library') {
                    const filename = selectedValue.replace('shared:', '');
                    formik.setFieldValue('applicationDetails.logoConfig.s3Location', AppLogoService.buildSharedLogoS3Location(filename));
                  } else {
                    formik.setFieldValue('applicationDetails.logoConfig.s3Location', null);
                  }
                }}
                options={logoOptions}
                filteringType="auto"
                placeholder="Search logos..."
                empty="No matching logos found"
              />
            </FormField>
          ) : (
            <FormField
              label="Upload Logo"
              description="Upload your own logo for this application."
              constraintText="Accepted formats: SVG, PNG. Max size: 1 MB."
              errorText={
                uploadError ||
                (getIn(formik.touched, 'applicationDetails.logoConfig.uploadedFileName') &&
                  (getIn(formik.errors, 'applicationDetails.logoConfig.uploadedFileName') ||
                    getIn(formik.errors, 'applicationDetails.logoConfig.s3Location')))
              }
            >
              <SpaceBetween size="xs" direction="vertical">
                <FileUpload
                  value={logoFile}
                  onChange={async ({ detail }) => {
                    const file = detail.value[0];
                    setUploadError(null);

                    if (file) {
                      const validationError = AppLogoService.validateFile(file);
                      if (validationError) {
                        setUploadError(validationError);
                        return;
                      }

                      setLogoFile(detail.value);
                      setIsUploading(true);

                      const result = await AppLogoService.uploadToTemp(file, sessionIdRef.current);
                      setIsUploading(false);

                      if (result.success && result.tempS3Location) {
                        formik.setFieldValue('applicationDetails.logoConfig.uploadedFileName', file.name);
                        formik.setFieldValue('applicationDetails.logoConfig.s3Location', result.tempS3Location);
                        if (previewUrl) URL.revokeObjectURL(previewUrl);
                        setPreviewUrl(URL.createObjectURL(file));
                      } else {
                        setUploadError(result.error || 'Upload failed.');
                        setLogoFile([]);
                      }
                    } else {
                      setLogoFile([]);
                      setPreviewUrl(null);
                      formik.setFieldValue('applicationDetails.logoConfig.uploadedFileName', null);
                      formik.setFieldValue('applicationDetails.logoConfig.s3Location', null);
                    }
                  }}
                  accept=".svg,.png"
                  i18nStrings={{
                    uploadButtonText: () => 'Choose File',
                    dropzoneText: () => 'Drop file to upload',
                    removeFileAriaLabel: () => 'Remove file',
                    errorIconAriaLabel: 'Error'
                  }}
                  showFileSize
                />
                {isUploading && <StatusIndicator type="loading">Uploading logo...</StatusIndicator>}
                {isDeleting && <StatusIndicator type="loading">Removing logo...</StatusIndicator>}
                {!logoFile.length && formik.values.applicationDetails?.logoConfig?.uploadedFileName && (
                  <SpaceBetween size="xs" direction="horizontal" alignItems="center">
                    <StatusIndicator type="success">{formik.values.applicationDetails.logoConfig.uploadedFileName}</StatusIndicator>
                    <Button
                      variant="inline-link"
                      formAction="none"
                      onClick={async (event) => {
                        event.preventDefault();
                        const s3Location = formik.values.applicationDetails?.logoConfig?.s3Location;
                        if (s3Location) {
                          setIsDeleting(true);
                          await AppLogoService.deleteLogo(s3Location);
                          setIsDeleting(false);
                        }
                        originalLogoConfigRef.current = null;
                        setPreviewUrl(null);
                        formik.setFieldValue('applicationDetails.logoConfig.uploadedFileName', null);
                        formik.setFieldValue('applicationDetails.logoConfig.s3Location', null);
                      }}
                    >
                      Remove Logo
                    </Button>
                  </SpaceBetween>
                )}
              </SpaceBetween>
            </FormField>
          )}
        </SpaceBetween>
      </ColumnLayout>

      {formik.values.applicationDetails?.logoConfig?.selectedLogoName || formik.values.applicationDetails?.logoConfig?.uploadedFileName ? (
        <FormField label="Preview">
          <EmbeddedNavPreview
            selectedLogoName={formik.values.applicationDetails?.logoConfig?.selectedLogoName || null}
            previewUrl={previewUrl}
            displayName={formik.values.applicationDetails?.applicationName || 'App Name'}
          />
        </FormField>
      ) : null}
    </SpaceBetween>
  );
};

export default ApplicationLogoConfig;
